// ===========================================================================
// CDCI — Capa de servicio (FAKE, contrato real).
//
// Cada función replica el endpoint MCM.CORE previsto en el documento maestro
// (02_CDCI_Descripcion_Modulo.md §6 — mapa página → endpoints). Hoy resuelven
// contra fakeDb.js; cuando el backend exponga api/amd/* el swap es 1:1 por
// función sin tocar las páginas.
// ===========================================================================
import {
  FAKE_LOCATIONS,
  FAKE_PROVIDERS,
  FAKE_PATIENTS,
  FAKE_APPOINTMENTS,
  FAKE_NOTE_TEMPLATES,
  FAKE_PROC_CODES,
  FAKE_DIAG_CODES,
  sessionStore,
  nextNoteId,
  simulateLatency,
} from "./fakeDb";
import { NOTE_STATUS, CHARGE_STATUS } from "../common/constants";

const patientById = (id) => FAKE_PATIENTS.find((p) => p.patientId === id);
const locationById = (id) => FAKE_LOCATIONS.find((l) => l.locationId === id);
const providerById = (id) => FAKE_PROVIDERS.find((pr) => pr.providerId === id);

/**
 * Obtiene los maestros de locations (réplica de mdm.TAB_AMD_LOCATIONS).
 *
 * FUTURO ENDPOINT (✅ código backend ya entregado según doc §4.6):
 * GET api/amd/locations
 *
 * Respuesta exitosa (200):
 * [ { "locationId": 1, "code": "SP-FM", "name": "Sante Plus — Fort Myers",
 *     "pos": "11", "active": true, "payloads": { "location": "...", ... } } ]
 *
 * Posibles errores: 401 sin JWT; 502 ProblemDetails si el sync falla.
 */
export async function getLocations() {
  await simulateLatency(120);
  return FAKE_LOCATIONS.filter((l) => l.active);
}

/**
 * Obtiene los providers disponibles (mientras se define la asociación
 * médico↔location en MCM — pendiente #6 del doc).
 *
 * FUTURO ENDPOINT: GET api/amd/providers
 */
export async function getProviders() {
  await simulateLatency(120);
  return FAKE_PROVIDERS;
}

/**
 * Citas del día del médico, filtradas por provider/location/fecha (página 4.1
 * "My Schedule" — Paso 1 del journey). Incluye, derivado del estado de sesión,
 * el estado de la nota y de los cargos por cita para los indicadores del grid.
 *
 * FUTURO ENDPOINT (💡 a diseñar con la entidad Appointment):
 * GET api/amd/appointments?providerId=101&locationId=1&date=2026-06-12
 * (detrás: REST Scheduler /scheduler/Appointments + maestros desde SQL)
 *
 * Respuesta exitosa (200):
 * [ { "appointmentId": 9001, "time": "08:00", "patient": {...},
 *     "typeKey": "AWV", "statusKey": "CHECKED_IN",
 *     "noteStatus": "DRAFT", "chargeStatus": "NOT_CAPTURED" } ]
 *
 * Posibles errores: 401; 429 ProblemDetails si se agota el rate limit Tier;
 * 502 si AdvancedMD no responde.
 */
export async function getAppointments({ providerId, locationId, date } = {}) {
  await simulateLatency();
  return FAKE_APPOINTMENTS.filter(
    (a) =>
      (!providerId || a.providerId === providerId) &&
      (!locationId || a.locationId === locationId)
  ).map((a) => {
    const note = sessionStore.notes.get(a.appointmentId);
    const charge = sessionStore.charges.get(a.appointmentId);
    return {
      ...a,
      date: date || null,
      statusKey: sessionStore.apptStatus.get(a.appointmentId) || a.statusKey,
      patient: patientById(a.patientId),
      provider: providerById(a.providerId),
      location: locationById(a.locationId),
      noteStatus: note ? note.status : NOTE_STATUS.NOT_STARTED.key,
      chargeStatus: charge ? charge.status : CHARGE_STATUS.NOT_CAPTURED.key,
    };
  });
}

/**
 * Contexto completo del encuentro en UNA llamada (página 4.2 — Paso 2).
 * Reúne cita + demografía/seguros + alergias + medicamentos + labs + notas
 * previas, que hoy serían 5 llamadas separadas a los GET clínicos ✅.
 *
 * FUTURO ENDPOINT (💡 agregador propuesto en doc §4.2):
 * GET api/amd/encounters/{appointmentId}/context
 *
 * Respuesta exitosa (200):
 * { "appointment": {...}, "patient": { "demographics": {...},
 *   "insurance": {...}, "allergies": [...], "medications": [...],
 *   "labs": [...], "previousNotes": [...], "activeProblems": [...] } }
 *
 * Posibles errores: 404 si la cita no existe; 429/502 como el resto.
 */
export async function getEncounterContext(appointmentId) {
  await simulateLatency(450);
  const appt = FAKE_APPOINTMENTS.find((a) => a.appointmentId === Number(appointmentId));
  if (!appt) throw new Error(`Appointment ${appointmentId} not found`);
  const note = sessionStore.notes.get(appt.appointmentId);
  const charge = sessionStore.charges.get(appt.appointmentId);
  return {
    appointment: {
      ...appt,
      statusKey: sessionStore.apptStatus.get(appt.appointmentId) || appt.statusKey,
      provider: providerById(appt.providerId),
      location: locationById(appt.locationId),
    },
    patient: patientById(appt.patientId),
    note: note || null,
    charges: charge || null,
  };
}

/**
 * Catálogo de plantillas de nota con su estructura de páginas/campos
 * (página 4.3). Equivale al descubrimiento getehrtemplates +
 * getehrnotestemplate del submódulo Note (doc §5.5).
 *
 * FUTURO ENDPOINT: GET api/amd/notes/templates
 */
export async function getNoteTemplates() {
  await simulateLatency(200);
  return FAKE_NOTE_TEMPLATES;
}

/**
 * Crea la nota del encuentro PRELLENADA (Paso 3 del journey): inyecta
 * location/location1/location2 desde los payloads del maestro de locations,
 * más paciente, visita y fecha — el médico nunca re-digita lo que el
 * sistema ya sabe.
 *
 * FUTURO ENDPOINT: POST api/amd/notes   (detrás: addehrnote)
 * Body: { "appointmentId": 9004, "templateName": "Hospital Discharge Follow Up" }
 *
 * Respuesta exitosa (201):
 * { "noteId": 33001, "templateName": "...", "status": "DRAFT",
 *   "fields": { "location": "Sante Plus Fort Myers", ... } }
 *
 * Posibles errores: 422 ProblemDetails con el Fault de AdvancedMD
 * (ej. template inexistente o visitid inválido).
 */
export async function createNote({ appointmentId, templateName }) {
  await simulateLatency(500);
  const appt = FAKE_APPOINTMENTS.find((a) => a.appointmentId === Number(appointmentId));
  const template = FAKE_NOTE_TEMPLATES.find((t) => t.templateName === templateName);
  if (!appt || !template) throw new Error("Invalid appointment or template");

  const location = locationById(appt.locationId);
  const patient = patientById(appt.patientId);
  const prefillSources = {
    location: location.payloads.location,
    location1: location.payloads.location1,
    location2: location.payloads.location2,
    visitDate: new Date().toLocaleDateString("en-US"),
    patientName: patient.name,
  };

  // Prellenado: los campos readonly toman su valor del payload correspondiente
  const fields = {};
  template.pages.forEach((page) =>
    page.fields.forEach((f) => {
      if (f.type === "readonly") {
        fields[f.fieldname] = prefillSources[f.prefill] ?? f.prefill ?? "";
      }
    })
  );

  const note = {
    noteId: nextNoteId(),
    appointmentId: appt.appointmentId,
    templateName,
    status: NOTE_STATUS.DRAFT.key,
    fields,
    signedAt: null,
    updatedAt: new Date().toISOString(),
  };
  sessionStore.notes.set(appt.appointmentId, note);
  return note;
}

/**
 * Guarda avances de la nota (Paso 4). Solo viajan los campos editados, con su
 * fieldname/ordinal exactos de la plantilla.
 *
 * FUTURO ENDPOINT: PUT api/amd/notes/{noteId}   (detrás: updateehrnote)
 * Body: { "fields": { "assessment": "...", "plan": "..." } }
 *
 * Posibles errores: 409 si la nota ya está firmada; 422 Fault AMD.
 */
export async function updateNote(appointmentId, fieldsPatch) {
  await simulateLatency(300);
  const note = sessionStore.notes.get(Number(appointmentId));
  if (!note) throw new Error("Note not found");
  if (note.status === NOTE_STATUS.SIGNED.key) throw new Error("Note is already signed");
  note.fields = { ...note.fields, ...fieldsPatch };
  note.updatedAt = new Date().toISOString();
  sessionStore.notes.set(Number(appointmentId), note);
  return { ...note };
}

/**
 * Búsqueda de códigos CPT (página 4.4). OBLIGATORIA antes de guardar cargos:
 * savecharges necesita el ID INTERNO del procedure code, no el CPT.
 *
 * FUTURO ENDPOINT: GET api/amd/charges/proc-codes?q=99213
 * (detrás: lookupproccode, con cache local de los códigos frecuentes)
 */
export async function lookupProcCodes(query = "") {
  await simulateLatency(200);
  const q = query.trim().toLowerCase();
  if (!q) return FAKE_PROC_CODES;
  return FAKE_PROC_CODES.filter(
    (p) => p.cpt.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
  );
}

/**
 * Búsqueda de diagnósticos ICD-10 (página 4.4). Los ICD viajan por código.
 *
 * FUTURO ENDPOINT: GET api/amd/charges/diag-codes?q=e11
 * (detrás: lookupdiagcode / lookupdiagnosiscode)
 */
export async function lookupDiagCodes(query = "") {
  await simulateLatency(200);
  const q = query.trim().toLowerCase();
  if (!q) return FAKE_DIAG_CODES;
  return FAKE_DIAG_CODES.filter(
    (d) => d.code.toLowerCase().includes(q) || d.description.toLowerCase().includes(q)
  );
}

/**
 * Guarda los cargos del encuentro (Paso 5 — workflow oficial AdvancedMD:
 * visita existente → lookupproccode → savecharges → verificación).
 *
 * FUTURO ENDPOINT: POST api/amd/charges   (detrás: savecharges, Tier 2 —
 * 12/min en pico; el rate limiter del núcleo ya lo gestiona)
 * Body: { "appointmentId": 9004,
 *   "procedures": [{ "procId": 70023, "cpt": "99214", "units": 1,
 *                    "modifiers": ["25"], "diagnosisCodes": ["E11.42","I10"] }],
 *   "diagnoses": [{ "code": "E11.42" }, { "code": "I10" }] }
 *
 * Respuesta exitosa (201): { "chargeId": ..., "status": "CAPTURED" }
 * Posibles errores: 422 Fault (procId inválido, visita sin abrir);
 * 429 si se supera el Tier 2.
 */
export async function saveCharges(appointmentId, { procedures, diagnoses }) {
  await simulateLatency(550);
  const record = {
    appointmentId: Number(appointmentId),
    status: CHARGE_STATUS.CAPTURED.key,
    procedures,
    diagnoses,
    savedAt: new Date().toISOString(),
  };
  sessionStore.charges.set(Number(appointmentId), record);
  return { ...record };
}

/**
 * Guarda borrador local de cargos (aún sin enviar a AdvancedMD).
 * No tiene endpoint: es estado de trabajo del front hasta el savecharges.
 */
export async function draftCharges(appointmentId, { procedures, diagnoses }) {
  const record = {
    appointmentId: Number(appointmentId),
    status: CHARGE_STATUS.DRAFT.key,
    procedures,
    diagnoses,
    savedAt: null,
  };
  sessionStore.charges.set(Number(appointmentId), record);
  return { ...record };
}

/**
 * Firma y cierra el encuentro (Pasos 6-7): valida el checklist, marca la nota
 * como firmada y la cita como completada.
 *
 * FUTURO ENDPOINT: POST api/amd/notes/{noteId}/sign
 * (mecanismo exacto de firma vía updateehrnote ⏳ por confirmar — pendiente #3
 * del doc; los metadatos de firma ya existen en AmdEhrNoteDto)
 *
 * Posibles errores: 409 checklist incompleto; 422 Fault AMD.
 */
export async function signAndClose(appointmentId) {
  await simulateLatency(600);
  const id = Number(appointmentId);
  const note = sessionStore.notes.get(id);
  if (!note) throw new Error("Cannot sign: the note has not been started");
  note.status = NOTE_STATUS.SIGNED.key;
  note.signedAt = new Date().toISOString();
  sessionStore.notes.set(id, note);
  sessionStore.apptStatus.set(id, "COMPLETED");
  return { ...note };
}

/**
 * Métricas del día para la signal bar de My Schedule (parte de la fase 6
 * "Inteligencia" del roadmap — visitas sin cargos vía getvisitswithnocharges).
 *
 * FUTURO ENDPOINT: GET api/amd/appointments/summary?providerId&date
 */
export async function getDaySummary({ providerId, locationId } = {}) {
  const appts = await getAppointments({ providerId, locationId });
  const active = appts.filter((a) => !["CANCELLED", "NO_SHOW"].includes(a.statusKey));
  const completed = active.filter((a) => a.statusKey === "COMPLETED");
  const notesPending = active.filter((a) => a.noteStatus !== "SIGNED").length;
  const chargesPending = active.filter((a) => a.chargeStatus !== "CAPTURED").length;
  const unbilledCompleted = completed.filter((a) => a.chargeStatus !== "CAPTURED").length;
  return {
    total: appts.length,
    active: active.length,
    completed: completed.length,
    notesPending,
    chargesPending,
    unbilledCompleted,
  };
}
